import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import StbMarkdown from '../../../../security-toolbox-ui/StbMarkdown';
import { stringifyValue } from './runDiagnosticsUtils';

export const CopyButton: React.FC<{
  value?: any;
  label?: string;
}> = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    const text = typeof value === 'string' ? value : stringifyValue(value, '');
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <span>
        <Button
          size="small"
          variant="text"
          startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={copy}
          disabled={value === null || value === undefined || value === ''}
        >
          {copied ? 'Copied' : label}
        </Button>
      </span>
    </Tooltip>
  );
};

export const WrappedText: React.FC<{
  children?: React.ReactNode;
  maxHeight?: number | string;
  label?: string;
  copyValue?: any;
}> = ({ children, maxHeight = 340, label, copyValue }) => (
  <Box>
    {label ? (
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={0.75}>
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
        <CopyButton value={copyValue ?? children} />
      </Stack>
    ) : null}

    <Box
      sx={{
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        maxHeight,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {children || '-'}
    </Box>
  </Box>
);

export const MarkdownBox: React.FC<{
  children?: string | null;
  maxHeight?: number | string;
  label?: string;
  copyValue?: any;
}> = ({ children, maxHeight = 520, label, copyValue }) => (
  <Box>
    {label ? (
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={0.75}>
        <Typography variant="subtitle2" fontWeight={700}>
          {label}
        </Typography>
        <CopyButton value={copyValue ?? children} />
      </Stack>
    ) : null}

    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        maxHeight,
        overflow: 'auto',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        '& p': { my: 0.75 },
        '& ul, & ol': { pl: 3, my: 1 },
        '& li': { mb: 0.5 },
        '& pre': {
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          maxWidth: '100%',
        },
        '& code': {
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        },
        '& table': {
          display: 'block',
          maxWidth: '100%',
          overflowX: 'auto',
        },
      }}
    >
      {children ? (
        <StbMarkdown>{children}</StbMarkdown>
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )}
    </Box>
  </Box>
);

export const DetailField: React.FC<{
  label: string;
  value?: React.ReactNode;
}> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>

    <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
      {value === null || value === undefined || value === '' ? '-' : value}
    </Typography>
  </Paper>
);

export const DiagnosticCard: React.FC<{
  icon?: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  status?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ icon, title, subtitle, status, children }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          {icon ? <Box sx={{ pt: 0.25 }}>{icon}</Box> : null}

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>

            {subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {status}
      </Stack>

      {children}
    </Stack>
  </Paper>
);

export const DiagnosticAccordion: React.FC<{
  defaultExpanded?: boolean;
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  chips?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ defaultExpanded, title, subtitle, icon, chips, children }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ width: '100%', pr: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {icon}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>

            {subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {chips ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {chips}
          </Stack>
        ) : null}
      </Stack>
    </AccordionSummary>

    <AccordionDetails>
      {children}
    </AccordionDetails>
  </Accordion>
);

export const JsonTree: React.FC<{
  value: any;
  maxHeight?: number | string;
}> = ({ value, maxHeight = 420 }) => (
  <WrappedText maxHeight={maxHeight} copyValue={value}>
    {stringifyValue(value)}
  </WrappedText>
);

export const RagChunksList: React.FC<{
  chunks: any[];
}> = ({ chunks }) => {
  if (!chunks.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No RAG chunks recorded.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {chunks.map((chunk, index) => {
        const metadata = chunk?.metadata || chunk?.meta || {};
        const source =
          chunk?.source ||
          chunk?.file_name ||
          chunk?.document_name ||
          chunk?.title ||
          metadata?.source ||
          metadata?.file_name ||
          metadata?.title ||
          `Chunk ${index + 1}`;
        const score = chunk?.score ?? chunk?.relevance ?? chunk?.rerank_score;
        const text =
          chunk?.text ||
          chunk?.chunk ||
          chunk?.content ||
          chunk?.page_content ||
          chunk?.document ||
          '';

        return (
          <Paper key={`${source}-${index}`} variant="outlined" sx={{ p: 1.25 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`#${index + 1}`} />
                <Chip size="small" variant="outlined" label={String(source)} />
                {score !== undefined && score !== null ? (
                  <Chip size="small" variant="outlined" color="primary" label={`score ${score}`} />
                ) : null}
              </Stack>

              {text ? (
                <WrappedText maxHeight={180} copyValue={text}>
                  {text}
                </WrappedText>
              ) : null}

              {metadata && Object.keys(metadata).length ? (
                <WrappedText label="Metadata" maxHeight={160} copyValue={metadata}>
                  {JSON.stringify(metadata, null, 2)}
                </WrappedText>
              ) : null}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};